import React, { useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from '@reach/router';
import { get, map, flatten, head, toLower } from '../../lib/nodash';
import { Box, Button, Heading } from 'grommet';
import styled from 'styled-components';

import ImgixImage from '../ImgixImage';
import IconAdd from '../Icons/IconAdd';
import IconQuestionMark from '../Icons/IconQuestionMark';
import ButtonAddToCart from '../ButtonAddToCart';

import useBuybarUi from '../Product/useBuybarUi';
import useMobile from '../useMobile';
import { activateProductVariantSelector } from '../../state/product/productSlice';
import { confirmSelectedProducts } from '../../state/calculator/calculatorSlice';
import { findOneGalVariant } from '../../lib/product';
import { track } from '../../lib/analytics';
import {
  SAMPLE_ADDED,
  PDP_CALCULATE,
} from '../../lib/analytics/segmentActions';

const InvertableButton = styled(Button)`
  color: ${(p) =>
    p.isDark
      ? p.theme.global.colors.text.dark
      : p.theme.global.colors.text.light};
  svg {
    fill: ${(p) =>
      p.isDark
        ? p.theme.global.colors.text.dark
        : p.theme.global.colors.text.light};
  }
`;

const ResponsiveButton = styled(Button)`
  @media only screen ${(p) =>
      p.theme.global.breakpoints.small.value &&
      `and (max-width: ${p.theme.global.breakpoints.small.value}px)`} {
    padding: ${(p) => parseInt(p.theme.global.edgeSize.small) * 1.5}px;
  }
`;

const ResponsiveA2CButton = styled(ButtonAddToCart)`
  @media only screen ${(p) =>
      p.theme.global.breakpoints.small.value &&
      `and (max-width: ${p.theme.global.breakpoints.small.value}px)`} {
    padding: ${(p) => parseInt(p.theme.global.edgeSize.small) * 1.5}px;
  }
`;

const PaintBuyOptions = ({
  sample,
  products,
  paintCollection,
  productType,
  dark,
  product,
  imageUrl,
  buttonText,
}) => {
  const dispatch = useDispatch();
  const isMobile = useMobile();
  const location = useLocation();
  const isAtTop = useSelector((state) => state.ui.scrolledToTop);
  const isPrimer = toLower(productType) === 'primer';
  const isCeilingPaint = product && toLower(product.title).includes('ceiling');
  const { buybarRef } = useBuybarUi();

  const oneGallonVariant = useMemo(
    () =>
      findOneGalVariant(
        product ? get('variants', product) : flatten(map('variants', products))
      ),
    [products]
  );

  const selectorOptions = {
    paintCollectionId:
      get('shopifyId', paintCollection) ?? get('id', paintCollection),
    variantId:
      get('shopifyId', oneGallonVariant) ?? get('id', oneGallonVariant),
    productType: get('productType', head(products)),
    altProducts: products,
    paintCollection: paintCollection,
  };

  const handleAddProduct = useCallback(() => {
    dispatch(activateProductVariantSelector(selectorOptions));
  });

  const handleSampleAdded = useCallback(() => {
    track(SAMPLE_ADDED, {
      product: get('title', paintCollection),
      pdp: true,
      cdp: false,
      location: location.pathname,
    });
  });

  const handleCalculator = useCallback(() => {
    track(PDP_CALCULATE, { paint: get('title', paintCollection) });
    dispatch(confirmSelectedProducts([product.shopifyId]));
  }, [dispatch]);

  return (
    <Box gap="medium" fill={isMobile ? 'horizontal' : false}>
      {isAtTop && (
        <Box>
          <InvertableButton
            size="xsmall"
            label="Calculate what you need"
            icon={<IconQuestionMark />}
            color="transparent"
            isDark={dark}
            onClick={handleCalculator}
          />
        </Box>
      )}
      <Box
        direction="row-responsive"
        gap={isMobile ? 0 : isAtTop ? 'medium' : 'none'}
        ref={buybarRef}
      >
        <Box>
          {!isAtTop && (
            <Box
              animation={{ type: 'fadeIn', duration: 500 }}
              direction="row"
              fill="vertical"
              border={{ side: 'all', size: 'small' }}
              style={
                isMobile ? { borderBottom: 'none' } : { borderRight: 'none' }
              }
              background="white"
              pad={{ left: isPrimer ? 'medium' : 'small', right: 'medium' }}
              gap="small"
              align="center"
              justify={isMobile ? 'start' : 'center'}
            >
              {!isPrimer && (
                <Box flex={false} pad="xsmall">
                  <ImgixImage
                    width={isMobile ? '41px' : 'auto'}
                    src={imageUrl}
                    options={{ w: 47, h: 47, q: 70 }}
                  />
                </Box>
              )}
              <Heading margin="none" level={5}>
                {get('title', paintCollection)}
              </Heading>
            </Box>
          )}
        </Box>
        <Box direction="row" gap={isMobile ? 0 : isAtTop ? 'medium' : 'none'}>
          <Box
            width={isMobile ? 'auto' : '220px'}
            flex={isMobile ? true : false}
          >
            <ResponsiveButton
              size="small"
              label={
                buttonText
                  ? buttonText
                  : isCeilingPaint
                  ? 'Add Paint'
                  : isPrimer
                  ? 'Add Primer'
                  : 'Add Paint'
              }
              reverse
              icon={<IconAdd />}
              onClick={handleAddProduct}
              primary={isPrimer}
              secondary={!isPrimer}
            />
          </Box>
          {sample && (
            <Box
              width={isMobile ? 'auto' : '220px'}
              flex={isMobile ? true : false}
            >
              <ResponsiveA2CButton
                quantity={1}
                variantId={get('shopifyId', sample)}
                size="small"
                label="Get Sample"
                primary
                onAdded={handleSampleAdded}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

PaintBuyOptions.propTypes = {
  sample: PropTypes.object,
  products: PropTypes.array,
  product: PropTypes.object,
  paintCollection: PropTypes.object,
  productType: PropTypes.string,
  dark: PropTypes.bool,
  imageUrl: PropTypes.string,
  buttonText: PropTypes.string,
};

export default memo(PaintBuyOptions);
